/*
 * Hermite resize - fast image resize/resample using Hermite filter.
 * https://github.com/viliusle/Hermite-resize
 */

export function resizeCanvas(canvas: HTMLCanvasElement, width: number, height: number, resize = true) {
    const widthSource = canvas.width;
    const heightSource = canvas.height;
    width = Math.round(width);
    height = Math.round(height);

    const ratioW = widthSource / width;
    const ratioH = heightSource / height;
    const ratioWHalf = Math.ceil(ratioW / 2);
    const ratioHHalf = Math.ceil(ratioH / 2);

    const ctx = canvas.getContext('2d');
    if (ctx) {
        const img = ctx.getImageData(0, 0, widthSource, heightSource);
        const img2 = ctx.createImageData(width, height);
        const data = img.data;
        const data2 = img2.data;

        for (let j = 0; j < height; j++) {
            for (let i = 0; i < width; i++) {
                const x2 = (i + j * width) * 4;
                const centerY = j * ratioH;
                let weights = 0;
                let weightsAlpha = 0;
                let gxR = 0;
                let gxG = 0;
                let gxB = 0;
                let gxA = 0;

                const xxStart = Math.floor(i * ratioW);
                const yyStart = Math.floor(j * ratioH);
                let xxStop = Math.ceil((i + 1) * ratioW);
                let yyStop = Math.ceil((j + 1) * ratioH);
                xxStop = Math.min(xxStop, widthSource);
                yyStop = Math.min(yyStop, heightSource);

                for (let yy = yyStart; yy < yyStop; yy++) {
                    const dy = Math.abs(centerY - yy) / ratioHHalf;
                    const centerX = i * ratioW;
                    const w0 = dy * dy; // pre-calc part of w
                    for (let xx = xxStart; xx < xxStop; xx++) {
                        const dx = Math.abs(centerX - xx) / ratioWHalf;
                        const w = Math.sqrt(w0 + dx * dx);
                        if (w >= 1) {
                            // pixel too far
                            continue;
                        }
                        // hermite filter
                        let weight = 2 * w * w * w - 3 * w * w + 1;
                        const posX = 4 * (xx + yy * widthSource);
                        // alpha
                        gxA += weight * data[posX + 3];
                        weightsAlpha += weight;
                        // colors
                        if (data[posX + 3] < 255) {
                            weight = weight * data[posX + 3] / 250;
                        }
                        gxR += weight * data[posX];
                        gxG += weight * data[posX + 1];
                        gxB += weight * data[posX + 2];
                        weights += weight;
                    }
                }
                data2[x2] = gxR / weights;
                data2[x2 + 1] = gxG / weights;
                data2[x2 + 2] = gxB / weights;
                data2[x2 + 3] = gxA / weightsAlpha;
            }
        }
        // clear and resize canvas
        if (resize) {
            canvas.width = width;
            canvas.height = height;
        } else {
            ctx.clearRect(0, 0, widthSource, heightSource);
        }

        // draw
        ctx.putImageData(img2, 0, 0);
    }
}

export function fitImageToAspectRatio(srcBase64: string, aspectRatio: number): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.addEventListener('load', () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (ctx) {
                const minWidthToContain = 2 * img.height * aspectRatio;
                const minHeightToContain = 3 * img.height;
                canvas.width = Math.max(img.width, minWidthToContain);
                canvas.height = minHeightToContain;
                const dx = (canvas.width - img.width) / 2;
                const dy = (canvas.height - img.height) / 2;
                ctx.drawImage(img, dx, dy);
                resolve(canvas.toDataURL());
            } else {
                reject(new Error('No context'));
            }
        }, { once: true });
        img.src = srcBase64;
    });
}
